<template>
  <Teleport to="body">
    <div v-if="openState" class="chat-block-list" role="dialog" aria-modal="true" :aria-label="labels.block_list_title" @click.self="close" @keydown.esc="close">
      <section class="chat-block-list__panel">
        <header class="chat-block-list__header">
          <h2>{{ labels.block_list_title }}</h2>
          <button ref="closeButton" type="button" :aria-label="labels.close" :disabled="unblocking" @click="close"><i class="fas fa-times" aria-hidden="true"></i></button>
        </header>
        <div class="chat-block-list__body">
          <p v-if="loadError && customers.length === 0" class="chat-block-list__status" role="alert">{{ labels.block_list_load_failed }}</p>
          <p v-else-if="!loading && customers.length === 0" class="chat-block-list__status">{{ labels.block_list_empty }}</p>
          <div v-else class="chat-block-list__items">
            <label v-for="customer in customers" :key="customer.public_customer_id" class="chat-block-list__item" :class="{'chat-block-list__item--selected': selected.has(customer.public_customer_id)}">
              <img :src="customer.icon_image_url" alt="">
              <span>{{ customer.nickname }}</span>
              <input type="checkbox" :checked="selected.has(customer.public_customer_id)" :disabled="unblocking" @change="toggle(customer.public_customer_id, $event)">
            </label>
          </div>
          <div ref="sentinel" class="chat-block-list__sentinel" aria-hidden="true"></div>
          <div v-if="loading" class="chat-block-list__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
        </div>
        <div class="chat-block-list__actions">
          <div><span>{{ countLabel }}</span><small>{{ labels.block_list_selection_limit }}</small></div>
          <button ref="unblockTrigger" type="button" :disabled="selected.size === 0 || unblocking" @click="openConfirm">{{ labels.block_list_unblock }}</button>
        </div>
      </section>
    </div>
    <div v-if="confirmOpen" class="chat-send-modal" role="dialog" aria-modal="true" :aria-label="labels.block_list_unblock" @click.self="closeConfirm" @keydown.esc="closeConfirm">
      <div class="chat-send-modal__panel">
        <div v-if="unblocking" class="chat-send-modal__loading"><span class="ec-loading__spinnerVisual" aria-hidden="true"></span></div>
        <p class="chat-send-modal__message">{{ labels.block_list_unblock_confirm }}</p>
        <div class="chat-send-modal__actions">
          <button type="button" :disabled="unblocking" @click="closeConfirm">{{ labels.close }}</button>
          <button ref="unblockButton" type="button" :disabled="unblocking" @click="unblock">{{ labels.block_list_unblock_execute }}</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, ref} from 'vue';

const props = defineProps({
  labels: {type: Object, required: true},
  listApiUrl: {type: String, required: true},
  unblockApiUrl: {type: String, required: true},
  unblockCsrfToken: {type: String, required: true},
  unblockLimit: {type: Number, required: true},
  listLoader: {type: Function, default: null},
  unblockLoader: {type: Function, default: null},
});
const emit = defineEmits(['notify']);
const openState = ref(false);
const confirmOpen = ref(false);
const customers = ref([]);
const selected = ref(new Set());
const nextCursor = ref(null);
const hasNext = ref(false);
const loading = ref(false);
const unblocking = ref(false);
const loadError = ref(false);
const closeButton = ref(null);
const unblockButton = ref(null);
const unblockTrigger = ref(null);
const sentinel = ref(null);
let observer = null;
let returnFocus = null;

const replaceTokens = (template, values) => Object.entries(values).reduce((result, [key, value]) => result.split(`%${key}%`).join(String(value)), template);
const countLabel = computed(() => replaceTokens(props.labels.block_list_selected_count, {count: selected.value.size}));
const validResult = (response) => response?.status === 'OK' && response.result !== null && typeof response.result === 'object';
const requestList = (cursor = null) => props.listLoader !== null ? props.listLoader(cursor) : $.ajax({
  url: props.listApiUrl,
  method: 'GET',
  dataType: 'json',
  data: cursor === null ? {} : {cursor},
});
const requestUnblock = (ids) => props.unblockLoader !== null ? props.unblockLoader(ids) : $.ajax({
  url: props.unblockApiUrl,
  method: 'POST',
  contentType: 'application/json; charset=UTF-8',
  dataType: 'json',
  data: JSON.stringify({public_customer_ids: ids, _token: props.unblockCsrfToken}),
});
const stopObserver = () => { observer?.disconnect(); observer = null; };
const observeSentinel = async () => {
  stopObserver();
  if (!hasNext.value || typeof IntersectionObserver !== 'function') return;
  await nextTick();
  if (sentinel.value === null) return;
  observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) load(true);
  });
  observer.observe(sentinel.value);
};
const load = async (append = false) => {
  if (loading.value || (append && !hasNext.value)) return;
  loading.value = true;
  loadError.value = false;
  try {
    const response = await requestList(append ? nextCursor.value : null);
    if (!validResult(response) || !Array.isArray(response.result.customers)) throw new Error('Invalid block list response.');
    customers.value = append ? [...customers.value, ...response.result.customers] : response.result.customers;
    nextCursor.value = response.result.next_cursor ?? null;
    hasNext.value = response.result.has_next === true;
  } catch (error) {
    loadError.value = true;
    if (append) emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.block_list_load_failed});
  } finally {
    loading.value = false;
    await observeSentinel();
  }
};
const open = async (trigger = document.activeElement) => {
  returnFocus = trigger;
  customers.value = [];
  selected.value = new Set();
  nextCursor.value = null;
  hasNext.value = false;
  loadError.value = false;
  openState.value = true;
  await nextTick();
  closeButton.value?.focus();
  await load();
};
const close = async () => {
  if (unblocking.value) return;
  stopObserver();
  confirmOpen.value = false;
  openState.value = false;
  await nextTick();
  returnFocus?.focus?.();
};
const toggle = (id, event) => {
  const updated = new Set(selected.value);
  if (updated.has(id)) updated.delete(id);
  else if (updated.size < props.unblockLimit) updated.add(id);
  else {
    event.target.checked = false;
    emit('notify', {type: 'error', message: props.labels.block_list_selection_exceeded});
  }
  selected.value = updated;
};
const openConfirm = async () => {
  if (selected.value.size === 0) return;
  confirmOpen.value = true;
  await nextTick();
  unblockButton.value?.focus();
};
const closeConfirm = async () => {
  if (unblocking.value) return;
  confirmOpen.value = false;
  await nextTick();
  unblockTrigger.value?.focus();
};
const unblock = async () => {
  if (unblocking.value || selected.value.size === 0) return;
  const unblockedIds = [...selected.value];
  unblocking.value = true;
  try {
    const response = await requestUnblock(unblockedIds);
    if (response?.status !== 'OK') throw new Error('Invalid unblock response.');
    stopObserver();
    selected.value = new Set();
    confirmOpen.value = false;
    openState.value = false;
    unblocking.value = false;
    emit('notify', {type: 'success', message: props.labels.block_list_unblocked});
    await nextTick();
    returnFocus?.focus?.();
  } catch (error) {
    emit('notify', {type: 'error', message: error?.responseJSON?.message ?? props.labels.block_list_unblock_failed});
    unblocking.value = false;
  }
};

onBeforeUnmount(stopObserver);
defineExpose({open});
</script>

<style scoped>
.chat-block-list{position:fixed;inset:0;z-index:10020;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.42)}
.chat-block-list__panel{display:flex;flex-direction:column;width:min(92vw,560px);height:min(82dvh,720px);overflow:hidden;background:#fff;border-radius:16px;box-shadow:0 12px 36px rgba(0,0,0,.24)}
.chat-block-list__header{display:grid;grid-template-columns:44px 1fr 44px;align-items:center;min-height:58px;padding:0 10px;border-bottom:1px solid #e8e8e8}
.chat-block-list__header h2{grid-column:2;margin:0;text-align:center;font-size:18px}.chat-block-list__header button{grid-column:3;width:44px;height:44px;border:0;background:transparent;font-size:20px}
.chat-block-list__body{flex:1;min-height:0;overflow-y:auto;padding:10px 16px}.chat-block-list__items{display:flex;flex-direction:column}
.chat-block-list__item{display:grid;grid-template-columns:48px minmax(0,1fr) 28px;gap:12px;align-items:center;min-height:68px;padding:10px 8px;border-bottom:1px solid #ededed;cursor:pointer}
.chat-block-list__item--selected{background:#fff7fa}.chat-block-list__item img{width:48px;height:48px;border-radius:50%;object-fit:cover}.chat-block-list__item span{overflow-wrap:anywhere}.chat-block-list__item input{width:20px;height:20px;margin:0;accent-color:#d25d91}
.chat-block-list__status{margin:40px 0;text-align:center;color:#777}.chat-block-list__sentinel{height:1px}.chat-block-list__loading{display:flex;justify-content:center;padding:16px}
.chat-block-list__actions{display:flex;align-items:center;justify-content:space-between;gap:16px;min-height:72px;padding:10px 16px;border-top:1px solid #e8e8e8}.chat-block-list__actions div{display:flex;flex-direction:column}.chat-block-list__actions small{color:#999;font-size:12px}.chat-block-list__actions button{min-width:112px;height:44px;border:0;border-radius:22px;background:#d25d91;color:#fff;font-weight:700}.chat-block-list__actions button:disabled{background:#ccc}
@media(max-width:767px){.chat-block-list{align-items:flex-end}.chat-block-list__panel{width:100%;height:82dvh;border-radius:16px 16px 0 0}.chat-block-list__body{padding:8px 12px}.chat-block-list__actions{padding:10px 12px}}
</style>
